// The only module in the web app that calls fetch. Every call returns the
// @phoneware/lens-domain shape the server's route table promises, and every
// signed-in call carries the Identity Platform ID token as a Bearer header, and
// X-Omni-Act-As while this tab acts as someone (spec 3.5).
import { ACT_AS_END_REASONS, ACT_AS_HEADER } from "@phoneware/lens-domain";
import type {
  ActAsEndReason,
  ActingAsView,
  ActivityView,
  AuditEntityKind,
  AccountMatchRecord,
  AccountNumberReplaceRequest,
  AccountSuggestion,
  ApprovalDecision,
  AutotaskActionRequest,
  AwaitingBillingApprovalOrder,
  BillingApprovalRequest,
  BillingRequirements,
  AutotaskOwner,
  BillingLinkRequest,
  CatalogDismissal,
  CatalogPatchV2,
  CatalogProductV2,
  CategoryDecisionRequest,
  ContactInput,
  CustomerAccounts,
  CustomerBillingRequest,
  CustomerContact,
  CustomersReview,
  CustomerSummary,
  CustomerV2,
  Dealer,
  DealerInput,
  IntakeConfirmRequest,
  InviteRequest,
  FeedbackCommandInput,
  FeedbackCommentInput,
  FeedbackCommentResponse,
  FeedbackDraftCaptureInput,
  FeedbackDraftCaptureResponse,
  FeedbackDraftDiscardInput,
  FeedbackDraftResponse,
  FeedbackDraftSubmitInput,
  FeedbackDraftUpdateInput,
  FeedbackListInput,
  FeedbackListResponse,
  FeedbackRequestDetail,
  FeedbackSubmitInput,
  FeedbackSubmitResponse,
  LensUser,
  MacLineCandidate,
  MarkSoldRequest,
  Me,
  PersonAccounts,
  NsDealerSyncResult,
  NsDirectoryStatus,
  NsResellerAssociationRequest,
  NsResellerAssociationResult,
  OAuthGrantRecord,
  OneBillCandidate,
  Order,
  OrderPatch,
  PricedProduct,
  ProductInput,
  PublicConfig,
  PublicQuoteResponse,
  Quote,
  QuoteInput,
  QuoteIntakeSummary,
  QuoteIntakeView,
  QuoteStatus,
  QuoteTemplateDeleteInput,
  QuoteTemplateDetail,
  QuoteTemplateInput,
  QuoteTemplateUpdateInput,
  SendQuoteRequest,
  SignRequest,
  SyncApplyRequest,
  SyncPlan,
  SyncResult,
  SyncRun,
  SyncRunRequest,
  SyncStatus,
  VersionInfo,
} from "@phoneware/lens-domain";
import type {
  AddCustomerRequest,
  AssignCustomerRequestV2,
  BillingReviewRequest,
  BillingReviewResult,
  CategoryStatus,
  DealerCategoryRequest,
  OneBillAccountMatch,
} from "./api-customers.js";
import type { OAuthDecisionRequest, OAuthDecisionResult, OAuthRequestView } from "./api-oauth.js";
import type { IntakeConfirmResult, IntakeUpload } from "./api-intakes.js";
import type { SupportTicket } from "./mac-order.js";

/** A failed call: the HTTP status (0 when the server could not be reached), the server's own message, and its JSON body when it sent one. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** Supplies the ID token for signed-in calls. `force` asks the identity SDK for a fresh one. */
export type TokenSource = (force: boolean) => Promise<string | null>;

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiOptions {
  token: TokenSource;
  /** Defaults to the browser's fetch. Tests and the dev harness pass their own. */
  fetch?: FetchLike;
  /** Where this tab keeps an act-as session id. Defaults to sessionStorage; tests pass their own. */
  actAs?: ActAsSlot;
}

/** sessionStorage key for the act-as session id of this tab (spec 3.9). */
export const ACT_AS_KEY = "omni.actAs";

/** The act-as session this tab works under while a Super admin acts as someone: its id, or null. */
export interface ActAsSlot {
  get(): string | null;
  set(id: string | null): void;
}

/** The browser's slot: sessionStorage, so the session belongs to this tab and ends with it. */
export function sessionActAsSlot(): ActAsSlot {
  return {
    get: () => {
      try {
        return window.sessionStorage.getItem(ACT_AS_KEY);
      } catch {
        return null;
      }
    },
    set: (id) => {
      try {
        if (id) window.sessionStorage.setItem(ACT_AS_KEY, id);
        else window.sessionStorage.removeItem(ACT_AS_KEY);
      } catch {
        // Storage refused (a private window with storage off): the next call goes out as the admin.
      }
    },
  };
}

/** A slot held in memory, for tests. */
export function memoryActAsSlot(initial: string | null = null): ActAsSlot {
  let id = initial;
  return { get: () => id, set: (next) => (id = next) };
}

/** Omni stopped honoring this tab's act-as session (409 act_as_ended). The slot is already cleared. */
export interface ActAsEnded {
  sessionId: string;
  reason: ActAsEndReason | null;
}

/** DELETE /api/act-as/:id: when the session ended and why. */
export interface ActAsStopped {
  endedAt: string;
  endReason: ActAsEndReason;
}

/** Which record's act-as changes to read: a quote id, an order id, or a customer's Autotask id as text. */
export interface ActivityEntityFilter {
  kind: AuditEntityKind;
  id: string;
}

export type UserPatch = Partial<Pick<LensUser, "name" | "role" | "scope" | "status">>;

/** POST /api/catalog/dismissals. The server records who and when. */
export type CatalogDismissalRequest = Pick<CatalogDismissal, "source" | "ref" | "name">;

export interface LensApi {
  version(): Promise<VersionInfo>;
  config(): Promise<PublicConfig>;
  /** `next` brings the link back to a consent page ("/connect/<id>"); the server takes nothing else. */
  requestEmailLink(email: string, next?: string): Promise<void>;

  /** This tab's act-as session id. While it is set, every signed-in call carries X-Omni-Act-As. */
  readonly actAs: ActAsSlot;
  /** Called once when Omni answers 409 act_as_ended for this tab's session; the slot is cleared first. Returns the unsubscribe. */
  onActAsEnded(listener: (ended: ActAsEnded) => void): () => void;
  /** POST /api/act-as as the admin: starts acting as a dealer user. 403 names why Omni refuses (forbidden, cannot_act_as with a reason). */
  startActAs(email: string): Promise<ActingAsView>;
  /** DELETE /api/act-as/:id as the admin, so an already ended session still answers with how it ended. */
  stopActAs(id: string, reason: "stopped" | "signed_out"): Promise<ActAsStopped>;
  /** GET /api/activity: act-as sessions in the caller's scope, or only those that touched one record. */
  activity(entity?: ActivityEntityFilter): Promise<ActivityView>;

  feedbackRequests(input?: FeedbackListInput): Promise<FeedbackListResponse>;
  feedbackRequest(id: string): Promise<FeedbackRequestDetail>;
  submitFeedback(input: FeedbackSubmitInput): Promise<FeedbackSubmitResponse>;
  commentOnFeedback(id: string, input: FeedbackCommentInput): Promise<FeedbackCommentResponse>;
  startFeedback(id: string, input: FeedbackCommandInput): Promise<FeedbackRequestDetail>;
  retryFeedbackFiling(id: string, input: FeedbackCommandInput): Promise<FeedbackRequestDetail>;
  retryFeedbackRun(id: string, input: FeedbackCommandInput): Promise<FeedbackRequestDetail>;
  closeFeedback(id: string, input: FeedbackCommandInput): Promise<FeedbackRequestDetail>;
  /** GET /api/feedback/draft: the caller's own batch of saved screens, or null when they have none. */
  feedbackDraft(): Promise<FeedbackDraftResponse>;
  /** POST /api/feedback/draft/captures: saves one annotated screen to the batch, creating it with the first. A retry with the same clientCaptureId answers the stored screen. */
  saveFeedbackDraftCapture(input: FeedbackDraftCaptureInput): Promise<FeedbackDraftCaptureResponse>;
  /** PATCH /api/feedback/draft: the batch's description and screen notes. The draft is null once it holds nothing. */
  updateFeedbackDraft(input: FeedbackDraftUpdateInput): Promise<FeedbackDraftResponse>;
  /** DELETE /api/feedback/draft/captures/:captureId: removes one screen; the rest renumber. */
  removeFeedbackDraftCapture(captureId: string): Promise<FeedbackDraftResponse>;
  /** POST /api/feedback/draft/discard: drops the whole batch. */
  discardFeedbackDraft(input: FeedbackDraftDiscardInput): Promise<FeedbackDraftResponse>;
  /** POST /api/feedback/draft/submit: turns the batch into one submitted request. */
  submitFeedbackDraft(input: FeedbackDraftSubmitInput): Promise<FeedbackSubmitResponse>;
  /** GET /api/feedback/draft/captures/:captureId: one saved screen's image. */
  feedbackDraftCapture(captureId: string): Promise<Blob>;
  /** GET /api/feedback/requests/:id/captures/:captureId: one of a request's screens. */
  feedbackCapture(id: string, captureId: string): Promise<Blob>;

  me(): Promise<Me>;
  users(): Promise<LensUser[]>;
  invite(input: InviteRequest): Promise<LensUser>;
  updateUser(email: string, patch: UserPatch): Promise<LensUser>;

  /** GET /api/people/:email/accounts: the person's account in each product Phoneware sells. Omni checks first when it never has. */
  personAccounts(email: string): Promise<PersonAccounts>;
  /** Check again: POST /api/people/:email/accounts/refresh. */
  refreshPersonAccounts(email: string): Promise<PersonAccounts>;
  /** Not me: the suggested account becomes rejected and Omni stops suggesting it. The person's own accounts only. */
  dismissAccount(email: string, matchId: string): Promise<PersonAccounts>;
  /** Undo a Not me: the account is a suggestion again. Takes the match id or the account key the rejection names. The person's own accounts only. */
  undoAccount(email: string, matchIdOrKey: string): Promise<PersonAccounts>;

  /**
   * GET /api/customers/:id/accounts: the customer's account in each product Phoneware resells. Suggestions come only to a
   * caller with customer_accounts.review, whose read also refreshes Texting first; everyone else reads linked and matched.
   */
  customerAccounts(id: number): Promise<CustomerAccounts>;
  /** The accounts to confirm across every customer. customer_accounts.review only. */
  customerAccountSuggestions(): Promise<{ suggestions: AccountSuggestion[] }>;
  /** Confirm or reject a suggestion. 409 `account_claimed` with `heldBy` when another customer holds the account. customer_accounts.review only. */
  decideCustomerAccount(matchId: string, decision: "confirm" | "reject"): Promise<AccountMatchRecord>;
  /** Puts a decided account back in the queue. customer_accounts.review only. */
  undoCustomerAccount(matchId: string): Promise<AccountMatchRecord>;

  dealers(): Promise<Dealer[]>;
  createDealer(input: DealerInput): Promise<Dealer>;
  updateDealer(id: string, patch: Partial<DealerInput>): Promise<Dealer>;
  nsDirectory(): Promise<NsDirectoryStatus>;
  syncNsDealers(): Promise<NsDealerSyncResult>;
  setNsReseller(id: string, input: NsResellerAssociationRequest): Promise<NsResellerAssociationResult>;
  /** The Autotask people who can own a dealer's companies: active and licensed, by name. Ops only. */
  autotaskOwners(): Promise<AutotaskOwner[]>;
  /** Every Autotask category as the last sync read it, with the dealer that holds it. Ops only. */
  autotaskCategories(): Promise<CategoryStatus[]>;
  /** Links a dealer to a category, or with null unlinks it. 409 when the holder the screen saw has changed. Ops only. */
  setDealerCategory(id: string, input: DealerCategoryRequest): Promise<Dealer>;
  /** Creates a dealer named after an Autotask category and links it. Ops only. */
  dealerFromCategory(categoryId: number): Promise<Dealer>;

  /** GET /api/catalog as a dealer user sees it. */
  pricedCatalog(): Promise<PricedProduct[]>;
  /** GET /api/catalog as Phoneware ops sees it: the whole record, with its Autotask and OneBill link state. */
  fullCatalog(): Promise<CatalogProductV2[]>;
  /** Creates the product in Omni and in Autotask in one request. 502 when Autotask refuses; the body's `product` is saved as push_failed. Ops only. */
  createProduct(input: ProductInput): Promise<CatalogProductV2>;
  /** Adds an item someone created directly in Autotask. Ops only. */
  adoptFromAutotask(input: { kind: "product" | "service"; id: number }): Promise<CatalogProductV2>;
  /** Edits Omni's fields and writes the mirrored ones to Autotask. 409 when the product changed since `expectedUpdatedAt`; 422 when Enable is refused. Ops only. */
  patchCatalog(id: string, patch: CatalogPatchV2): Promise<CatalogProductV2>;
  /** Retry a push, keep Omni's values, take Autotask's, or create a missing item again. Ops only. */
  autotaskAction(id: string, input: AutotaskActionRequest): Promise<CatalogProductV2>;
  /** The five OneBill products closest by name, or with a query, OneBill products matching it by name or code. Ops only. */
  onebillCandidates(id: string, query?: string): Promise<OneBillCandidate[]>;
  /** Confirms the OneBill link, marks create with the OneBill plan whose setup it copies (#2156), marks not billed, or puts the item back to unreviewed. 422 names what OneBill can't copy from. Ops only. */
  setBillingLink(id: string, input: BillingLinkRequest): Promise<CatalogProductV2>;
  /** Items ops keep out of Omni: ignored Autotask items and OneBill products not quoted in Omni. Ops only. */
  catalogDismissals(): Promise<CatalogDismissal[]>;
  dismissCatalogItem(input: CatalogDismissalRequest): Promise<CatalogDismissal>;
  /** Puts a dismissed item back; `key` is "autotask:product-123" or "onebill:<code>". Ops only. */
  restoreCatalogItem(key: string): Promise<void>;
  syncPlan(): Promise<SyncPlan>;
  syncApply(input: SyncApplyRequest): Promise<SyncResult>;

  /** What the scheduled sync last read and wrote. Ops only. */
  syncStatus(): Promise<SyncStatus>;
  /** Runs the sync now. 409 while another run holds the lease. Ops only. */
  runSync(input: SyncRunRequest): Promise<SyncRun>;

  /** The caller's customers as Omni stored them at the last sync; no contacts, no Autotask call. */
  customers(): Promise<CustomerSummary[]>;
  /** One customer with its live Autotask contacts. */
  customer(id: number): Promise<CustomerV2>;
  /** Creates a Prospect in Autotask under the dealer's category. 409 with `duplicate` when Autotask already has it. */
  createCustomer(input: AddCustomerRequest): Promise<CustomerSummary>;
  assignCustomer(input: AssignCustomerRequestV2): Promise<CustomerSummary>;
  /** Links the customer's OneBill account, confirms it has none, or puts it back to not reviewed. Ops only. */
  setCustomerBilling(id: number, input: CustomerBillingRequest): Promise<CustomerSummary>;
  /** Settles a category Autotask changed: keep Omni's dealer, or move to the category's dealer. Ops only. */
  decideCategory(id: number, input: CategoryDecisionRequest): Promise<CustomerSummary>;
  /** Replaces the Autotask Number with an RPP number (4 to 6 digits) while the customer has no linked OneBill account. 409 when linked or when the customer changed since `expectedUpdatedAt`. Ops only. */
  replaceAccountNumber(id: number, input: AccountNumberReplaceRequest): Promise<CustomerV2>;
  /** Adds a contact to the customer in Autotask and reads it back. 409 when the email is already on the company. */
  addContact(id: number, input: ContactInput): Promise<CustomerContact>;
  customersReview(): Promise<CustomersReview>;
  decideBillingReview(accountNumber: string, input: BillingReviewRequest): Promise<BillingReviewResult>;
  /** OneBill accounts matching a name or number. Ops only. */
  onebillAccounts(query: string): Promise<OneBillAccountMatch[]>;

  quotes(status?: QuoteStatus): Promise<Quote[]>;
  quote(id: string): Promise<Quote>;
  createQuote(input: QuoteInput): Promise<Quote>;
  updateQuote(id: string, input: QuoteInput): Promise<Quote>;
  cloneQuote(id: string): Promise<Quote>;
  sendQuote(id: string, input: SendQuoteRequest): Promise<Quote>;
  quotePdf(id: string): Promise<Blob>;
  approvals(): Promise<Quote[]>;
  decideApproval(id: string, input: ApprovalDecision): Promise<Quote>;

  /** A customer's support tickets for a MAC order: created in the last 90 days or not Complete, newest first. Ops only. */
  customerTickets(id: number): Promise<SupportTicket[]>;
  /** A support ticket's charges matched to the catalog, as MAC order lines. Ops only. */
  ticketLines(ticketId: number): Promise<MacLineCandidate[]>;
  /**
   * Marks a MAC (change) quote sold and returns the order it creates, with no customer signature. 409 when the quote
   * changed since `expectedUpdatedAt`, a line isn't billable or the customer's OneBill account isn't linked. Ops only.
   */
  markSold(id: string, input: MarkSoldRequest): Promise<Order>;

  /** The caller's dealer's templates, each priced from today's catalog. Ops see every dealer's, or one dealer's with `dealerId`. */
  quoteTemplates(dealerId?: string): Promise<QuoteTemplateDetail[]>;
  quoteTemplate(id: string): Promise<QuoteTemplateDetail>;
  /** 409 when the dealer already has a template by that name. Ops name the dealer; a dealer's own people never do. */
  createQuoteTemplate(input: QuoteTemplateInput): Promise<QuoteTemplateDetail>;
  /** 409 when someone saved it after `expectedUpdatedAt`, or when the new name is taken. */
  updateQuoteTemplate(id: string, input: QuoteTemplateUpdateInput): Promise<QuoteTemplateDetail>;
  /** 409 when someone saved it after `expectedUpdatedAt`. */
  deleteQuoteTemplate(id: string, input: QuoteTemplateDeleteInput): Promise<void>;

  orders(): Promise<Order[]>;
  order(id: string): Promise<Order>;
  retryOrder(id: string): Promise<Order>;
  /**
   * Saves the due date and the numbers on the order (plan 6B). The due date goes on every ticket of the order; new numbers
   * add one note to the provisioning ticket, or the order ticket when there is none. 409 when the order changed since
   * `expectedUpdatedAt`. Only the fields that changed are sent, so an unchanged numbers note never adds a second note.
   * `lineNumbers` sets each line's Telephone Number on the order for billing approval (#1763): 400 naming the line,
   * 502 when OneBill can't say which lines need one, 409 once billing is approved.
   */
  patchOrder(id: string, patch: OrderPatch): Promise<Order>;
  /** Orders paused after their Autotask ticket, waiting on ops to approve billing, with their totals and the customer's OneBill state. Ops only. */
  ordersToBill(): Promise<AwaitingBillingApprovalOrder[]>;
  /**
   * What each line's OneBill plan needs entered before billing is approved, prefilled where Omni can (#1732): a Telephone
   * Number from the seller's number, else the customer's main number (#1763). 502 when OneBill can't say. Anyone who sees the order.
   */
  billingRequirements(id: string): Promise<BillingRequirements>;
  /**
   * Approves the OneBill account an order bills to and queues the run. 409 when the order changed since `expectedUpdatedAt`, 422 when OneBill or
   * Autotask can't take it, 400 naming the line and feature when a value the lines need is missing or malformed. Ops only.
   */
  approveBilling(id: string, input: BillingApprovalRequest): Promise<Order>;

  /** Who is asking to connect and when, read without signing in. 404 when Omni has no such request. */
  oauthRequest(id: string): Promise<OAuthRequestView>;
  /** The same read as the signed-in person: 403 with code "user_disabled" when their Omni sign-in is turned off. */
  oauthRequestAsMe(id: string): Promise<OAuthRequestView>;
  /** Allow or Deny with the person's ID token; answers the client's redirect. 410 once the request has expired. */
  decideOAuthRequest(id: string, input: OAuthDecisionRequest): Promise<OAuthDecisionResult>;
  /** The caller's own grants; an owner also gets their dealer's people's, ops every grant. */
  oauthGrants(): Promise<OAuthGrantRecord[]>;
  /** Revokes a grant the caller may see and answers it revoked, with revokedBy the caller's email. */
  revokeOAuthGrant(id: string): Promise<OAuthGrantRecord>;

  publicQuote(token: string): Promise<PublicQuoteResponse>;
  signQuote(token: string, input: SignRequest): Promise<PublicQuoteResponse>;
  /** The customer's PDF is public behind its token, so it is a plain link rather than a call. */
  publicPdfUrl(token: string): string;

  // Quote documents (#2018, C7). Lists are summaries. A document is the view for this caller.
  /** Open and past quote documents, newest first. Staff see every dealer's, or one dealer's with `dealerId`. */
  quoteIntakes(dealerId?: string): Promise<QuoteIntakeSummary[]>;
  quoteIntake(id: string): Promise<QuoteIntakeView>;
  /** One rendered part, as stored. PDF and image parts are the original when that is all there is. */
  quoteIntakePart(id: string, index: number): Promise<Blob>;
  /**
   * Sends the file as it is (its own type, named by X-Filename). `again` when the server answered 200 with the
   * reading it already holds for the same file from the same dealer. 413 over 10 MB, 415 when Omni can't read it.
   * Staff name the dealer.
   */
  uploadQuoteIntake(file: Blob, filename: string, dealerId?: string): Promise<IntakeUpload>;
  /** Pasted quote text or a conversation transcript. Staff name the dealer. */
  submitQuoteText(input: { text: string; textKind: "document" | "transcript"; title?: string | null; dealerId?: string }): Promise<IntakeUpload>;
  /** A KQM quote link, id or number. Phoneware staff only. Staff name the dealer. */
  importKqmQuote(input: { quote: string; dealerId?: string }): Promise<IntakeUpload>;
  /** Creates what the person checked. 400 naming what is missing creates nothing. */
  confirmQuoteIntake(id: string, input: IntakeConfirmRequest): Promise<IntakeConfirmResult>;
  /** 409 once it is confirmed. Answers the view. */
  discardQuoteIntake(id: string): Promise<QuoteIntakeView>;
}

type Expect = "json" | "none" | "blob";

interface Call {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
  signedIn: boolean;
  expect?: Expect;
  accept?: string;
  /** A file sent as it is, in place of a JSON body: its bytes, its type and the headers that describe it. */
  raw?: { body: Blob; contentType: string; headers: Record<string, string> };
  /** Goes out as the signed-in person even while this tab acts as someone: starting and stopping act-as. */
  asSelf?: boolean;
}

/** The 409 body for a session Omni no longer honors, read from an ApiError's body. */
function endedReason(body: unknown): { reason: ActAsEndReason | null } | null {
  if (!body || typeof body !== "object" || !("error" in body) || body.error !== "act_as_ended") return null;
  const reason = "reason" in body && typeof body.reason === "string" && (ACT_AS_END_REASONS as readonly string[]).includes(body.reason) ? (body.reason as ActAsEndReason) : null;
  return { reason };
}

async function readError(res: Response): Promise<{ message: string; body: unknown }> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body && typeof body.error === "string" && body.error) return { message: body.error, body };
  } catch {
    // Not JSON: fall through to the status line.
  }
  return { message: `Omni answered ${res.status}${res.statusText ? ` ${res.statusText}` : ""}.`, body: null };
}

export function createApi(options: ApiOptions): LensApi {
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));
  const slot = options.actAs ?? sessionActAsSlot();
  const endedListeners = new Set<(ended: ActAsEnded) => void>();

  async function send(call: Call, force: boolean, actAsId: string | null): Promise<Response> {
    const headers: Record<string, string> = { Accept: call.accept ?? (call.expect === "blob" ? "application/pdf" : "application/json") };
    if (call.raw) Object.assign(headers, call.raw.headers, { "Content-Type": call.raw.contentType });
    else if (call.body !== undefined) headers["Content-Type"] = "application/json";
    if (call.signedIn) {
      const token = await options.token(force);
      if (!token) throw new ApiError(401, "Sign in to continue.");
      headers.Authorization = `Bearer ${token}`;
      if (actAsId) headers[ACT_AS_HEADER] = actAsId;
    }
    try {
      return await doFetch(call.path, {
        method: call.method,
        headers,
        body: call.raw ? call.raw.body : call.body === undefined ? undefined : JSON.stringify(call.body),
      });
    } catch {
      throw new ApiError(0, "Omni could not be reached. Check your connection and try again.");
    }
  }

  /** The answer to a call that went through, or the ApiError the server's refusal names. */
  async function answer(call: Call): Promise<Response> {
    const actAsId = call.signedIn && !call.asSelf ? slot.get() : null;
    let res = await send(call, false, actAsId);
    // An ID token can expire between the SDK's refresh and our call; ask for a fresh one once.
    if (res.status === 401 && call.signedIn) res = await send(call, true, actAsId);
    if (!res.ok) {
      const failure = await readError(res);
      const ended = res.status === 409 && actAsId ? endedReason(failure.body) : null;
      // The first call to hear it clears the slot and tells the app once; calls already in flight find it cleared.
      if (ended && actAsId && slot.get() === actAsId) {
        slot.set(null);
        for (const listener of endedListeners) listener({ sessionId: actAsId, reason: ended.reason });
      }
      throw new ApiError(res.status, failure.message, failure.body);
    }
    return res;
  }

  async function run<T>(call: Call): Promise<T> {
    const res = await answer(call);
    const expect = call.expect ?? "json";
    if (expect === "none") return undefined as T;
    if (expect === "blob") return (await res.blob()) as T;
    return (await res.json()) as T;
  }

  const enc = encodeURIComponent;
  const get = <T>(path: string): Promise<T> => run<T>({ method: "GET", path, signedIn: true });
  const post = <T>(path: string, body?: unknown): Promise<T> => run<T>({ method: "POST", path, body: body ?? {}, signedIn: true });

  return {
    version: () => run({ method: "GET", path: "/api/version", signedIn: false }),
    config: () => run({ method: "GET", path: "/api/config", signedIn: false }),
    requestEmailLink: (email, next) => run({ method: "POST", path: "/api/auth/email-link", body: next ? { email, next } : { email }, signedIn: false, expect: "none" }),

    actAs: slot,
    onActAsEnded: (listener) => {
      endedListeners.add(listener);
      return () => endedListeners.delete(listener);
    },
    startActAs: (email) => run({ method: "POST", path: "/api/act-as", body: { email }, signedIn: true, asSelf: true }),
    stopActAs: (id, reason) => run({ method: "DELETE", path: `/api/act-as/${enc(id)}`, body: { reason }, signedIn: true, asSelf: true }),
    activity: (entity) =>
      get(entity ? `/api/activity?entityKind=${enc(entity.kind)}&entityId=${enc(entity.id)}` : "/api/activity"),

    feedbackRequests: (input) => {
      const query = new URLSearchParams();
      if (input?.scope?.kind === "platform") query.set("scope", "platform");
      else if (input?.scope?.kind === "dealer") query.set("dealerId", input.scope.dealerId);
      if (input?.mine !== undefined) query.set("mine", String(input.mine));
      if (input?.status) query.set("status", input.status);
      if (input?.cursor) query.set("cursor", input.cursor);
      if (input?.limit !== undefined) query.set("limit", String(input.limit));
      const suffix = query.size ? `?${query.toString()}` : "";
      return get(`/api/feedback/requests${suffix}`);
    },
    feedbackRequest: (id) => get(`/api/feedback/requests/${enc(id)}`),
    submitFeedback: (input) => post("/api/feedback/requests", input),
    commentOnFeedback: (id, input) => post(`/api/feedback/requests/${enc(id)}/comments`, input),
    startFeedback: (id, input) => post(`/api/feedback/requests/${enc(id)}/start`, input),
    retryFeedbackFiling: (id, input) => post(`/api/feedback/requests/${enc(id)}/retry-filing`, input),
    retryFeedbackRun: (id, input) => post(`/api/feedback/requests/${enc(id)}/retry-run`, input),
    closeFeedback: (id, input) => post(`/api/feedback/requests/${enc(id)}/close`, input),
    feedbackDraft: () => get("/api/feedback/draft"),
    saveFeedbackDraftCapture: (input) => post("/api/feedback/draft/captures", input),
    updateFeedbackDraft: (input) => run({ method: "PATCH", path: "/api/feedback/draft", body: input, signedIn: true }),
    removeFeedbackDraftCapture: (captureId) => run({ method: "DELETE", path: `/api/feedback/draft/captures/${enc(captureId)}`, signedIn: true }),
    discardFeedbackDraft: (input) => post("/api/feedback/draft/discard", input),
    submitFeedbackDraft: (input) => post("/api/feedback/draft/submit", input),
    feedbackDraftCapture: (captureId) =>
      run({ method: "GET", path: `/api/feedback/draft/captures/${enc(captureId)}`, signedIn: true, expect: "blob", accept: "image/png, image/webp" }),
    feedbackCapture: (id, captureId) =>
      run({ method: "GET", path: `/api/feedback/requests/${enc(id)}/captures/${enc(captureId)}`, signedIn: true, expect: "blob", accept: "image/png, image/webp" }),

    me: () => get("/api/me"),
    users: () => get("/api/users"),
    invite: (input) => post("/api/users", input),
    updateUser: (email, patch) => run({ method: "PATCH", path: `/api/users/${enc(email)}`, body: patch, signedIn: true }),

    personAccounts: (email) => get(`/api/people/${enc(email)}/accounts`),
    refreshPersonAccounts: (email) => post(`/api/people/${enc(email)}/accounts/refresh`),
    dismissAccount: (email, matchId) => post(`/api/people/${enc(email)}/accounts/${enc(matchId)}/dismiss`),
    undoAccount: (email, matchId) => post(`/api/people/${enc(email)}/accounts/${enc(matchId)}/undo`),

    customerAccounts: (id) => get(`/api/customers/${enc(String(id))}/accounts`),
    customerAccountSuggestions: () => get("/api/customers/accounts/review"),
    decideCustomerAccount: (matchId, decision) => post(`/api/customers/accounts/review/${enc(matchId)}`, { decision }),
    undoCustomerAccount: (matchId) => post(`/api/customers/accounts/review/${enc(matchId)}/undo`),

    dealers: () => get("/api/dealers"),
    createDealer: (input) => post("/api/dealers", input),
    updateDealer: (id, patch) => run({ method: "PATCH", path: `/api/dealers/${enc(id)}`, body: patch, signedIn: true }),
    nsDirectory: () => get("/api/dealers/ns-directory"),
    syncNsDealers: () => post("/api/dealers/ns-sync"),
    setNsReseller: (id, input) => run({ method: "PUT", path: `/api/dealers/${enc(id)}/ns-reseller`, body: input, signedIn: true }),
    autotaskOwners: () => get("/api/dealers/autotask-owners"),
    autotaskCategories: () => get("/api/dealers/autotask-categories"),
    setDealerCategory: (id, input) => run({ method: "PUT", path: `/api/dealers/${enc(id)}/autotask-category`, body: input, signedIn: true }),
    dealerFromCategory: (categoryId) => post("/api/dealers/from-autotask-category", { categoryId }),

    pricedCatalog: () => get("/api/catalog"),
    fullCatalog: () => get("/api/catalog"),
    createProduct: (input) => post("/api/catalog", input),
    adoptFromAutotask: (input) => post("/api/catalog/from-autotask", input),
    patchCatalog: (id, patch) => run({ method: "PATCH", path: `/api/catalog/${enc(id)}`, body: patch, signedIn: true }),
    autotaskAction: (id, input) => post(`/api/catalog/${enc(id)}/autotask`, input),
    onebillCandidates: (id, query) =>
      get(query ? `/api/catalog/${enc(id)}/onebill-candidates?q=${enc(query)}` : `/api/catalog/${enc(id)}/onebill-candidates`),
    setBillingLink: (id, input) => run({ method: "PUT", path: `/api/catalog/${enc(id)}/billing-link`, body: input, signedIn: true }),
    catalogDismissals: () => get("/api/catalog/dismissals"),
    dismissCatalogItem: (input) => post("/api/catalog/dismissals", input),
    restoreCatalogItem: (key) => run({ method: "DELETE", path: `/api/catalog/dismissals/${enc(key)}`, signedIn: true, expect: "none" }),
    syncPlan: () => post("/api/catalog/sync/plan"),
    syncApply: (input) => post("/api/catalog/sync/apply", input),

    syncStatus: () => get("/api/sync"),
    runSync: (input) => post("/api/sync/runs", input),

    customers: () => get("/api/customers"),
    customer: (id) => get(`/api/customers/${enc(String(id))}`),
    createCustomer: (input) => post("/api/customers", input),
    assignCustomer: (input) => post("/api/customers/assign", input),
    setCustomerBilling: (id, input) => run({ method: "PUT", path: `/api/customers/${enc(String(id))}/billing`, body: input, signedIn: true }),
    decideCategory: (id, input) => post(`/api/customers/${enc(String(id))}/category`, input),
    replaceAccountNumber: (id, input) => run({ method: "PUT", path: `/api/customers/${enc(String(id))}/account-number`, body: input, signedIn: true }),
    addContact: (id, input) => post(`/api/customers/${enc(String(id))}/contacts`, input),
    customersReview: () => get("/api/customers/review"),
    decideBillingReview: (accountNumber, input) => post(`/api/billing-review/${enc(accountNumber)}`, input),
    onebillAccounts: (query) => get(`/api/onebill/accounts?q=${enc(query)}`),

    quotes: (status) => get(status ? `/api/quotes?status=${enc(status)}` : "/api/quotes"),
    quote: (id) => get(`/api/quotes/${enc(id)}`),
    createQuote: (input) => post("/api/quotes", input),
    updateQuote: (id, input) => run({ method: "PUT", path: `/api/quotes/${enc(id)}`, body: input, signedIn: true }),
    cloneQuote: (id) => post(`/api/quotes/${enc(id)}/clone`),
    sendQuote: (id, input) => post(`/api/quotes/${enc(id)}/send`, input),
    quotePdf: (id) => run({ method: "GET", path: `/api/quotes/${enc(id)}/pdf`, signedIn: true, expect: "blob" }),
    approvals: () => get("/api/approvals"),
    decideApproval: (id, input) => post(`/api/quotes/${enc(id)}/approval`, input),

    customerTickets: (id) => get(`/api/customers/${enc(String(id))}/tickets`),
    ticketLines: (ticketId) => get(`/api/autotask-tickets/${enc(String(ticketId))}/lines`),
    markSold: (id, input) => post(`/api/quotes/${enc(id)}/sold`, input),

    quoteTemplates: (dealerId) => get(dealerId ? `/api/quote-templates?dealerId=${enc(dealerId)}` : "/api/quote-templates"),
    quoteTemplate: (id) => get(`/api/quote-templates/${enc(id)}`),
    createQuoteTemplate: (input) => post("/api/quote-templates", input),
    updateQuoteTemplate: (id, input) => run({ method: "PUT", path: `/api/quote-templates/${enc(id)}`, body: input, signedIn: true }),
    deleteQuoteTemplate: (id, input) => run({ method: "DELETE", path: `/api/quote-templates/${enc(id)}`, body: input, signedIn: true, expect: "none" }),

    orders: () => get("/api/orders"),
    order: (id) => get(`/api/orders/${enc(id)}`),
    retryOrder: (id) => post(`/api/orders/${enc(id)}/retry`),
    patchOrder: (id, patch) => run({ method: "PATCH", path: `/api/orders/${enc(id)}`, body: patch, signedIn: true }),
    ordersToBill: () => get("/api/orders?status=awaiting_billing_approval"),
    billingRequirements: (id) => get(`/api/orders/${enc(id)}/billing-requirements`),
    approveBilling: (id, input) => post(`/api/orders/${enc(id)}/billing-approval`, input),

    oauthRequest: (id) => run({ method: "GET", path: `/api/oauth/requests/${enc(id)}`, signedIn: false }),
    oauthRequestAsMe: (id) => get(`/api/oauth/requests/${enc(id)}`),
    decideOAuthRequest: (id, input) => post(`/api/oauth/requests/${enc(id)}/decision`, input),
    oauthGrants: () => get("/api/oauth/grants"),
    revokeOAuthGrant: (id) => run({ method: "DELETE", path: `/api/oauth/grants/${enc(id)}`, signedIn: true }),

    publicQuote: (token) => run({ method: "GET", path: `/api/q/${enc(token)}`, signedIn: false }),
    signQuote: (token, input) => run({ method: "POST", path: `/api/q/${enc(token)}/sign`, body: input, signedIn: false }),
    publicPdfUrl: (token) => `/api/q/${enc(token)}/pdf`,

    quoteIntakes: (dealerId) => get(dealerId ? `/api/quote-intakes?dealerId=${enc(dealerId)}` : "/api/quote-intakes"),
    quoteIntake: (id) => get(`/api/quote-intakes/${enc(id)}`),
    quoteIntakePart: (id, index) => run({ method: "GET", path: `/api/quote-intakes/${enc(id)}/parts/${index}`, signedIn: true, expect: "blob" }),
    uploadQuoteIntake: async (file, filename, dealerId) => {
      const type = file.type && file.type !== "application/octet-stream" ? file.type : "application/octet-stream";
      const res = await answer({
        method: "POST",
        path: dealerId ? `/api/quote-intakes?dealerId=${enc(dealerId)}` : "/api/quote-intakes",
        signedIn: true,
        raw: { body: file, contentType: type, headers: { "X-Filename": enc(filename) } },
      });
      return { intake: (await res.json()) as QuoteIntakeView, again: res.status === 200 };
    },
    submitQuoteText: async (input) => {
      const res = await answer({
        method: "POST",
        path: "/api/quote-intakes/text",
        body: input,
        signedIn: true,
      });
      return { intake: (await res.json()) as QuoteIntakeView, again: res.status === 200 };
    },
    importKqmQuote: async (input) => {
      const res = await answer({
        method: "POST",
        path: "/api/quote-intakes/kqm",
        body: input,
        signedIn: true,
      });
      return { intake: (await res.json()) as QuoteIntakeView, again: res.status === 200 };
    },
    confirmQuoteIntake: (id, input) => post(`/api/quote-intakes/${enc(id)}/confirm`, input),
    discardQuoteIntake: (id) => post(`/api/quote-intakes/${enc(id)}/discard`),
  };
}
